import React from 'react';
import { useGridApiContext } from '@mui/x-data-grid';
import { useDispatch, useSelector } from 'react-redux';
import { useLazySearchAuditActionsQuery } from '@/features/audit/auditApi';
import { useLazyBatchGetClientsQuery } from '@/features/clients/clientsApi';
import { useLazyBatchGetDealsQuery } from '@/features/deals/dealsApi';
import { useGetBrokerGroupUsersQuery, useGetUserDetailsQuery } from '@/features/user/userApi';
import { parseValueForCsv } from '@/components/tables/InstantSearchDataGrid/data';
import { ExportCsvButton } from '@/components/tables/export/ExportCsvButton';
import { downloadCsv } from '@/components/tables/export/downloadCsv';
import { enrichAuditRowsForExport, sortAuditRows } from './auditRowUtils';
import { showToast } from '@/features/toast/toastSlice';
import { useTableSort } from '@/context/tableSortContext';

export const AuditExportButton = ({ searchParams, disabled = false }) => {
	const dispatch = useDispatch();
	const apiRef = useGridApiContext();
	const { sortModel } = useTableSort();
	const [searchAuditActions] = useLazySearchAuditActionsQuery();
	const [getBatchClients] = useLazyBatchGetClientsQuery();
	const [getBatchDeals] = useLazyBatchGetDealsQuery();

	const account = useSelector((state) => state.msalAccount.account);
	const email = account?.username;
	const { data: userDetails } = useGetUserDetailsQuery({ email }, { skip: !email });
	const canViewGroup = userDetails?.canViewGroup;

	const brokerGroupUsersQuery = useGetBrokerGroupUsersQuery(
		{ userId: userDetails?.userId, showGroup: true },
		{ skip: !userDetails?.userId || !canViewGroup },
	);

	const brokerGroupUsers = (brokerGroupUsersQuery.data ?? []).reduce((acc, current) => {
		if (!acc.find((item) => item.id === current.id)) {
			acc.push(current);
		}
		return acc;
	}, []);

	const handleExport = async () => {
		const response = await searchAuditActions(searchParams).unwrap();
		const rows = response?.data ?? [];

		if (!rows.length) {
			dispatch(showToast({ message: 'No audit records to export' }));
			return;
		}

		const enrichedRows = await enrichAuditRowsForExport({
			rows,
			getBatchClients,
			getBatchDeals,
		});

		downloadCsv({
			columns: apiRef.current.getVisibleColumns(),
			rows: sortAuditRows(enrichedRows, sortModel),
			getValue: (column, row) => parseValueForCsv({ column, brokerGroupUsers, value: row }),
			fileName: 'Audit',
		});
	};

	return <ExportCsvButton onExport={handleExport} disabled={disabled} />;
};
